/**
 * stripImageMetadata.js — client-side EXIF / GPS / device-tag scrubber.
 *
 * Why this lives in the browser, not the relay:
 *   The relay never sees plaintext bytes, so it physically cannot
 *   strip EXIF post-encryption. The only place metadata-scrubbing
 *   can happen is here, *before* AES-GCM wrapping. If we don't do
 *   it here, GPS coords, camera serials, and capture timestamps
 *   ride into ciphertext and decrypt back into the recipient's
 *   gallery on the other side.
 *
 * How it works:
 *   We re-encode the image through a `<canvas>`. Canvas-encoded
 *   output is a freshly-built bitmap — every chunk that isn't
 *   pixel data (EXIF/IPTC/XMP/ICC/orientation) is dropped on the
 *   floor. We preserve the original mime when possible (jpeg/png/
 *   webp) and fall back to PNG for exotic formats.
 *
 * Heads-up:
 *   - Animated GIFs lose their animation (we keep frame 1). That's
 *     a deliberate trade — silent metadata leakage > novelty
 *     animation. If we ever want to keep animation we'd need a
 *     proper GIF decoder; not worth it for v1.
 *   - SVGs are returned unchanged because SVG is XML, not raster,
 *     and Canvas would rasterize away the vector data. SVG metadata
 *     stripping is a different problem (XML node walk) — out of
 *     scope here. UI should warn for `image/svg+xml`.
 *   - We respect EXIF Orientation by drawing through `createImageBitmap`
 *     with `imageOrientation: 'from-image'` so the visual output
 *     looks the same as before — without persisting the orientation
 *     tag in the output.
 */

const PASSTHROUGH = new Set(['image/svg+xml', 'image/gif']);

const fileToBitmap = async (file) =>
  createImageBitmap(file, { imageOrientation: 'from-image' });

const canvasToBlob = (canvas, mime, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null')),
      mime,
      quality,
    );
  });

/**
 * Strip metadata from an image File and return a new File (same name,
 * size in bytes will differ — usually smaller, occasionally larger
 * for tiny PNGs that lose compression metadata).
 *
 * Returns the original File untouched when:
 *   - It is not an image (mime check)
 *   - It is in the PASSTHROUGH set (svg/gif — see notes above)
 *   - Canvas decoding fails (we fail open to "send original" rather
 *     than block the user; we mark `stripped: false` on the result so
 *     UI can warn).
 *
 * @param {File} file
 * @returns {Promise<{file: File, stripped: boolean, reason?: string}>}
 */
export async function stripImageMetadata(file) {
  if (!file || !file.type || !file.type.startsWith('image/')) {
    return { file, stripped: false, reason: 'not-an-image' };
  }
  if (PASSTHROUGH.has(file.type)) {
    return { file, stripped: false, reason: `passthrough:${file.type}` };
  }

  try {
    const bitmap = await fileToBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close?.();

    // Preserve original mime for jpeg/png/webp; everything else gets PNG.
    let outMime = file.type;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(outMime)) {
      outMime = 'image/png';
    }
    // Use 0.92 quality for jpeg/webp (matches Canvas browser default
    // and is visually indistinguishable from typical phone-camera output).
    const quality = outMime === 'image/png' ? undefined : 0.92;
    const blob = await canvasToBlob(canvas, outMime, quality);

    // Preserve filename for the recipient; switch extension if mime changed.
    let filename = file.name;
    if (outMime !== file.type) {
      const ext = outMime === 'image/png' ? '.png' : '.jpg';
      filename = filename.replace(/\.[^.]+$/, '') + ext;
    }
    const cleanFile = new File([blob], filename, {
      type: outMime,
      lastModified: Date.now(), // also drops the original lastModified leak
    });
    return { file: cleanFile, stripped: true };
  } catch (err) {
    // Fail open: never block the user from sending. UI surfaces a
    // warning so the human can decide.
    return { file, stripped: false, reason: `error:${err?.message || err}` };
  }
}
